import React, { useContext, useEffect, useState, useRef } from 'react';
import { getApiUrl } from '../utils/api';
import { AppContext } from '../contexts/AppContext';
import { useInterface } from '../contexts/InterfaceContext';
import io, { Socket } from 'socket.io-client';


interface ProductGroup {
  id: string;
  groupId: string;
  groupName: string;
  infeedDisplayName: string;
}

interface ProductGroupSession {
  id: string;
  productGroupId: string;
  groupId: string;
  groupName: string;
  lineId?: string;
  lineName?: string;
  shiftNumber: number;
  productionTimeSeconds: number;
  isActive: boolean;
}

interface Line {
  id: string;
  lineName: string;
  lineNumber: number;
  isActive: boolean;
}

const ProductionModePanel: React.FC = () => {
  // Use shared context for selected line
  const { selectedLineId, setSelectedLineId } = useInterface();
  
  // Use the same hostname and port as the current page
  const hostname = window.location.hostname || 'localhost';
  const port = window.location.port ? `:${window.location.port}` : '';
  const apiUrl = getApiUrl();
  const [currentMode, setCurrentMode] = useState<string>('stop');
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [activeProductGroup, setActiveProductGroup] = useState<ProductGroupSession | null>(null);
  const [lineProductGroups, setLineProductGroups] = useState<Record<string, ProductGroupSession>>({});
  const [availableGroups, setAvailableGroups] = useState<ProductGroup[]>([]);
  const [availableLines, setAvailableLines] = useState<Line[]>([]);
  const [selectedLine, setSelectedLine] = useState<string | null>(selectedLineId);
  const [selectedProductGroup, setSelectedProductGroup] = useState<string | null>(null);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [currentShift, setCurrentShift] = useState<number | null>(null);
  const [lineModes, setLineModes] = useState<Record<string, string>>({});
  const [showLineSelector, setShowLineSelector] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const modes = [
    {
      id: 'stop',
      name: 'Stop Mode',
      description: 'Halts all operations, turns off all stations, locking the system in an idle state.',
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      selectedColor: 'bg-red-600',
      textColor: 'text-red-700',
      borderColor: 'border-red-500'
    },
    {
      id: 'prepare',
      name: 'Prepare Mode',
      description: 'Facilitates station bin filling without initiating weighing operations or time accumulation.',
      color: 'bg-yellow-500',
      hoverColor: 'hover:bg-yellow-600',
      selectedColor: 'bg-yellow-600',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-500'
    },
    {
      id: 'production',
      name: 'Production Mode',
      description: 'Engages all active filling, weighing, and time accumulation operations.',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      selectedColor: 'bg-green-600',
      textColor: 'text-green-700',
      borderColor: 'border-green-500'
    },
    {
      id: 'production-standby',
      name: 'Production Standby Mode',
      description: 'Suspends station bin filling while continuing weighing and time accumulation.',
      color: 'bg-amber-500',
      hoverColor: 'hover:bg-amber-600',
      selectedColor: 'bg-amber-600',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-500'
    },
    {
      id: 'clean',
      name: 'Clean Mode',
      description: 'Configures the system for maintenance, opening all doors and disabling operations.',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      selectedColor: 'bg-blue-600',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-500'
    }
  ];

  // Click outside handler to collapse menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't collapse if clicking the toggle button or inside the panel
      if (!isCollapsed && 
          panelRef.current && 
          !panelRef.current.contains(event.target as Node) &&
          toggleButtonRef.current &&
          !toggleButtonRef.current.contains(event.target as Node)) {
        setIsCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCollapsed]);

  // Socket.io for real-time updates
  useEffect(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.hostname || 'localhost'}${window.location.port ? `:${window.location.port}` : ''}`;
    
    socketRef.current = io(wsUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      console.log('ProductionModePanel WebSocket connected');
    });

    socketRef.current.on('mode-changed', (data: { lineId: string; newMode: string; previousMode: string }) => {
      console.log('Received mode change via WebSocket:', data);
      setLineModes(prev => {
        const updated = {
          ...prev,
          [data.lineId]: data.newMode
        };
        console.log('Updated lineModes state from WebSocket:', updated);
        return updated;
      });
      
      // Also fetch fresh data to ensure consistency with server state
      // Note: fetchActiveProductGroup will also update lineModes from session data
      fetchActiveProductGroup();
    });

    socketRef.current.on('product-group-created', () => {
      fetchAvailableGroups();
      fetchActiveProductGroup();
    });

    socketRef.current.on('product-group-updated', () => {
      fetchAvailableGroups();
      fetchActiveProductGroup();
    });

    socketRef.current.on('product-group-deleted', () => {
      fetchAvailableGroups();
      fetchActiveProductGroup();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    console.log('ProductionModePanel mounted, apiUrl:', apiUrl);
    // System mode deprecated - using line-specific modes
    fetchCurrentShift();
    fetchAvailableGroups();
    fetchAvailableLines();
    // fetchLineModes(); // Now getting modes from session data in fetchActiveProductGroup
  }, [apiUrl]);
  
  // Debug effect to log when lineModes changes
  useEffect(() => {
    console.log('lineModes state changed:', lineModes);
  }, [lineModes]);
  
  // Separate fetch on mount/change from polling to avoid conflicts
  useEffect(() => {
    console.log('Initial fetch triggered - currentShift:', currentShift, 'selectedLine:', selectedLine);
    if (currentShift && selectedLine) {
      fetchActiveProductGroup();
    }
  }, [currentShift, selectedLine]); // Fetch when shift or line changes
  
  // Poll for line changes every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAvailableLines();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Poll for shift changes every 10 seconds (shift transitions are rare)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCurrentShift();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Separate polling interval with longer delay to reduce conflicts
  useEffect(() => {
    if (currentShift && selectedLine) {
      let interval: NodeJS.Timeout;
      
      // Start polling after initial fetch with 3-second interval to reduce conflicts
      // Add a delay before starting to avoid immediate conflicts
      const timeout = setTimeout(() => {
        interval = setInterval(() => {
          console.log('Polling update for product groups');
          fetchActiveProductGroup();
        }, 3000);
      }, 1000); // Wait 1 second before starting polling
      
      return () => {
        clearTimeout(timeout);
        if (interval) clearInterval(interval);
      };
    }
  }, [currentShift, selectedLine]); // Restart polling when dependencies change

  // Select first line when lines are loaded
  useEffect(() => {
    if (availableLines.length > 0 && !selectedLine) {
      setSelectedLine(availableLines[0].id);
      setSelectedLineId(availableLines[0].id); // Update context for Dashboard
    }
  }, [availableLines]);
  
  // Fetch line modes when selectedLine changes to update button display
  useEffect(() => {
    if (selectedLine) {
      console.log('Selected line changed, fetching line modes for:', selectedLine);
      fetchLineModes();
    }
  }, [selectedLine]);
  
  // Note: activeProductGroup is updated by fetchActiveProductGroup
  // We don't need a separate effect here as it causes conflicts

  // DEPRECATED: System mode is no longer used
  // Line modes are fetched separately per line
  
  const fetchLineModes = async () => {
    try {
      console.log('Fetching line modes from:', `${apiUrl}/line-production-modes`);
      const response = await fetch(`${apiUrl}/line-production-modes`);
      const result = await response.json();
      console.log('Line modes response:', result);
      if (result.message === 'success' && result.data) {
        // result.data is an object keyed by line_id with mode info
        const modes: Record<string, string> = {};
        Object.keys(result.data).forEach(lineId => {
          modes[lineId] = result.data[lineId].mode;
        });
        console.log('Setting lineModes to:', modes);
        setLineModes(modes);
      }
    } catch (error) {
      console.error('Error fetching line modes:', error);
    }
  };
  
  const fetchCurrentShift = async () => {
    try {
      const response = await fetch(`${apiUrl}/production-status`);
      const result = await response.json();
      if (result.message === 'success' && result.data?.currentShift) {
        setCurrentShift(result.data.currentShift.number);
      }
    } catch (error) {
      console.error('Error fetching current shift:', error);
    }
  };
  
  const fetchActiveProductGroup = async () => {
    console.log('fetchActiveProductGroup called - selectedLine:', selectedLine, 'currentShift:', currentShift);
    try {
      if (!currentShift) {
        console.log('No current shift, skipping fetch');
        return;
      }
      
      // Add timestamp to debug timing issues
      const fetchTime = new Date().toISOString();
      console.log(`[${fetchTime}] Fetching active-all for shift ${currentShift}`);
      
      // Fetch all active product group sessions
      const response = await fetch(`${apiUrl}/product-group-sessions/active-all?shift=${currentShift}`);
      
      if (!response.ok) {
        console.error('Failed to fetch active groups:', response.status, response.statusText);
        return;
      }
      
      const result = await response.json();
      console.log('Active product group sessions response:', JSON.stringify(result, null, 2));
      if (result.message === 'success' && result.data) {
        // result.data should be an array of all active sessions
        const groupsByLine: Record<string, ProductGroupSession> = {};
        const modesByLine: Record<string, string> = {};
        if (Array.isArray(result.data)) {
          result.data.forEach((session: ProductGroupSession) => {
            if (session.lineId) {
              groupsByLine[session.lineId] = session;
              // Update line modes from session data
              modesByLine[session.lineId] = session.currentMode || 'stop';
              console.log(`Session for line ${session.lineId}:`, session.groupName || session.productGroupName, 'mode:', session.currentMode, 'time:', session.productionTimeSeconds);
            } else {
              console.warn('Session missing lineId:', session);
            }
          });
        } else if (result.data.lineId) {
          // Single session returned (backward compatibility)
          groupsByLine[result.data.lineId] = result.data;
          modesByLine[result.data.lineId] = result.data.currentMode || 'stop';
        }
        setLineProductGroups(groupsByLine);
        
        // Update line modes from session data (source of truth)
        console.log('Updating lineModes from session data:', modesByLine);
        setLineModes(modesByLine);
        
        // Set active product group for selected line
        console.log('Current selectedLine:', selectedLine, 'Groups by line:', Object.keys(groupsByLine));
        console.log('Full groupsByLine:', JSON.stringify(groupsByLine, null, 2));
        
        if (selectedLine && groupsByLine[selectedLine]) {
          console.log('ProductionModePanel: Setting active product group for', selectedLine, ':', {
            groupName: groupsByLine[selectedLine].groupName,
            lineId: groupsByLine[selectedLine].lineId,
            currentMode: groupsByLine[selectedLine].currentMode,
            productionTimeSeconds: groupsByLine[selectedLine].productionTimeSeconds
          });
          setActiveProductGroup(groupsByLine[selectedLine]);
        } else {
          // Don't immediately clear - might be a timing issue
          console.log('ProductionModePanel: No product group found for line', selectedLine);
          console.log('Available lines in response:', Object.keys(groupsByLine));
          console.log('Type of selectedLine:', typeof selectedLine);
          if (Object.keys(groupsByLine).length > 0) {
            console.log('Type of first key:', typeof Object.keys(groupsByLine)[0]);
          }
          // Only clear if we're certain there's no product group
          // Don't clear if this is just a timing issue during assignment
          if (!activeProductGroup || activeProductGroup.lineId !== selectedLine) {
            console.log('Clearing activeProductGroup because no match found');
            setActiveProductGroup(null);
          } else {
            console.log('Keeping existing activeProductGroup to avoid flicker');
          }
        }
      } else {
        console.warn('Invalid response or no data:', result);
        setLineProductGroups({});
        setActiveProductGroup(null);
      }
    } catch (error) {
      console.error('Error fetching active Product Groups:', error);
      console.error('Error details:', error.message, error.stack);
      // Don't clear data on network errors - keep last known state
    }
  };
  
  const fetchAvailableGroups = async () => {
    try {
      const response = await fetch(`${apiUrl}/product-group-sessions/available-groups`);
      const result = await response.json();
      if (result.message === 'success' && result.data) {
        setAvailableGroups(result.data);
      }
    } catch (error) {
      console.error('Error fetching available Product Groups:', error);
    }
  };
  
  const fetchAvailableLines = async () => {
    try {
      const response = await fetch(`${apiUrl}/lines`);
      const result = await response.json();
      if (result.message === 'success' && result.data) {
        const activeLines = result.data.filter((line: Line) => line.isActive);
        setAvailableLines(activeLines);
      }
    } catch (error) {
      console.error('Error fetching available Lines:', error);
    }
  };
  
  const handleProductGroupSwitch = async (groupId: string) => {
    let shiftToUse = currentShift;
    
    // If currentShift is not set yet, fetch it synchronously
    if (!shiftToUse) {
      console.log('Current shift not set, fetching it now...');
      try {
        const response = await fetch(`${apiUrl}/production-status`);
        const result = await response.json();
        if (result.message === 'success' && result.data?.currentShift) {
          shiftToUse = result.data.currentShift.number;
          setCurrentShift(shiftToUse); // Update state for future use
          console.log('Fetched current shift:', shiftToUse);
        }
      } catch (error) {
        console.error('Error fetching current shift:', error);
        alert('Unable to determine current shift. Please try again.');
        return;
      }
    }
    
    setIsLoading(true);
    try {
      const requestBody = {
        productGroupId: groupId,
        shiftNumber: shiftToUse,
        lineId: selectedLine,
        currentMode: activeProductGroup?.currentMode || 'stop'  // Use active group's mode or default to stop
      };
      console.log('Switching Product Group with request:', requestBody);
      
      const response = await fetch(`${apiUrl}/product-group-sessions/switch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      const result = await response.json();
      console.log('Switch Product Group response:', response.status, result);
      
      // Check for both possible success messages or if there's an error
      if (!response.ok) {
        console.error('Failed to switch Product Group:', result.error || result);
        alert(`Failed to switch Product Group: ${result.error || 'Unknown error'}`);
      } else if ((result.message === 'Product Group session started' || result.message === 'success') && result.data) {
        console.log('Product group assigned successfully:', result.data);
        
        // Update local state immediately with the response data
        const sessionData = result.data;
        // Ensure lineId is set correctly
        if (!sessionData.lineId && selectedLine) {
          sessionData.lineId = selectedLine;
        }
        
        setActiveProductGroup(sessionData);
        setShowGroupSelector(false);
        
        // Update lineProductGroups to include this new assignment
        setLineProductGroups(prev => ({
          ...prev,
          [selectedLine]: sessionData
        }));
        
        // Always sync the session mode with the current line mode
        if (result.data.id && selectedLine) {
          const currentLineMode = lineModes[selectedLine] || 'stop';
          await fetch(`${apiUrl}/product-group-sessions/${result.data.id}/mode`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ mode: currentLineMode }),
          });
          
          // Delay the refresh to avoid immediate clearing
          setTimeout(() => {
            fetchActiveProductGroup();
          }, 1000);
        }
      } else {
        console.error('Unexpected response:', result);
      }
    } catch (error) {
      console.error('Error switching Product Group:', error);
      console.error('Error details:', error.message, error.stack);
      alert(`Failed to switch Product Group: ${error.message || 'Network error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassignProductGroup = async () => {
    if (!selectedLine || !activeProductGroup) return;
    
    console.log('Unassigning product group:', activeProductGroup.groupName, 'from line:', selectedLine);
    setIsLoading(true);
    try {
      // End the current session
      if (activeProductGroup?.id) {
        console.log('Ending session:', activeProductGroup.id);
        const response = await fetch(`${apiUrl}/product-group-sessions/${activeProductGroup.id}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        const result = await response.json();
        console.log('End session response:', result);
        
        if (response.ok) {
          // Clear the line's product group assignment
          console.log('Clearing line product group assignment');
          await fetch(`${apiUrl}/lines/${selectedLine}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ current_product_group_id: null })
          });
          
          // Update local state immediately
          setActiveProductGroup(null);
          setLineProductGroups(prev => {
            const updated = { ...prev };
            delete updated[selectedLine];
            return updated;
          });
          
          // Also clear the line mode
          setLineModes(prev => {
            const updated = { ...prev };
            delete updated[selectedLine];
            return updated;
          });
          
          // Close the modal
          setShowGroupSelector(false);
          
          // Fetch fresh data to ensure consistency
          setTimeout(() => {
            fetchActiveProductGroup();
          }, 500);
        } else {
          console.error('Failed to end session:', result);
        }
      }
    } catch (error) {
      console.error('Error unassigning product group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModeChange = async (newMode: string) => {
    if (!selectedLine) {
      console.error('No line selected');
      return;
    }
    
    const currentLineMode = lineModes[selectedLine] || 'stop';
    console.log('Mode change requested for line:', selectedLine, 'New:', newMode, 'Current:', currentLineMode);
    if (newMode === currentLineMode || isLoading) return;
    
    setIsLoading(true);
    try {
      // Update line-specific mode
      console.log('Sending PUT request to:', `${apiUrl}/line-modes/${selectedLine}`);
      const response = await fetch(`${apiUrl}/line-modes/${selectedLine}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          mode: newMode
        }),
      });
      
      const result = await response.json();
      console.log('Mode update response:', result);
      if (result.message === 'success') {
        // Update the mode for this specific line immediately for responsive UI
        setLineModes(prev => ({
          ...prev,
          [selectedLine]: newMode
        }));
        
        // Refresh the active product groups to get the updated state from server
        // This will also update lineModes with the actual server state
        await fetchActiveProductGroup();
      } else {
        console.error('Failed to update system mode:', result.error);
      }
    } catch (error) {
      console.error('Error updating system mode:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        ref={toggleButtonRef}
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`fixed top-2 z-50 bg-indigo-600 hover:bg-indigo-700 text-white border-2 border-indigo-700 rounded-l-lg px-3 py-4 shadow-xl transition-all duration-300 ${
          isCollapsed ? 'right-0' : 'right-80'
        }`}
        title={isCollapsed ? 'Show System Modes' : 'Hide System Modes'}
      >
        <div className="flex flex-col items-center space-y-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2.5} 
              d={isCollapsed ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
            />
          </svg>
          <span className="text-xs font-semibold" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            {isCollapsed ? '' : 'HIDE'}
          </span>
        </div>
      </button>

      {/* Panel */}
      <div ref={panelRef} className={`fixed right-0 top-0 h-full w-80 bg-white shadow-lg border-l border-gray-200 flex flex-col transition-transform duration-300 ease-in-out ${
        isCollapsed ? 'translate-x-full' : 'translate-x-0'
      }`}>
        <div className="p-3 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Production Control</h2>
        </div>
      
      <div className="flex-1 overflow-hidden p-3">
        {/* Line Selector Section - Compact */}
        <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-blue-900">Production Line</h3>
            {currentShift && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                Shift {currentShift}
              </span>
            )}
          </div>
          
          {availableLines.length > 0 ? (
            <button
              onClick={() => setShowLineSelector(true)}
              className="w-full px-4 py-3 bg-white border-2 border-blue-400 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  {selectedLine ? (
                    <>
                      <div className="font-semibold text-blue-900">
                        {availableLines.find(l => l.id === selectedLine)?.lineName}
                      </div>
                      {availableLines.find(l => l.id === selectedLine)?.lineNumber && (
                        <div className="text-xs text-blue-600">
                          Line #{availableLines.find(l => l.id === selectedLine)?.lineNumber}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-blue-700">Select a line...</div>
                  )}
                </div>
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
          ) : (
            <p className="text-sm text-blue-700 text-center py-2">No active lines available</p>
          )}
        </div>

        {/* Product Group Section */}
        <div className="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-indigo-900">Product Group</h3>
          </div>
          
          {(() => {
            console.log('Product Group Display Check:', {
              activeProductGroup: activeProductGroup,
              activeProductGroupLineId: activeProductGroup?.lineId,
              selectedLine: selectedLine,
              lineIdMatch: activeProductGroup?.lineId === selectedLine,
              hasActiveGroup: !!activeProductGroup
            });
            return activeProductGroup && activeProductGroup.lineId === selectedLine;
          })() ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-indigo-800">{activeProductGroup.groupName}</p>
                  <p className="text-xs text-indigo-600">{activeProductGroup.groupId}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedProductGroup(activeProductGroup?.productGroupId || null);
                    fetchAvailableGroups(); // Refresh product groups when opening selector
                    setShowGroupSelector(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors min-w-[80px]"
                  disabled={isLoading}
                >
                  Change
                </button>
              </div>
              {activeProductGroup.productionTimeSeconds > 0 && (
                <div className="text-xs text-indigo-600">
                  Production Time: {Math.floor(activeProductGroup.productionTimeSeconds / 3600) > 0 && `${Math.floor(activeProductGroup.productionTimeSeconds / 3600)}h `}
                  {Math.floor((activeProductGroup.productionTimeSeconds % 3600) / 60)}m {activeProductGroup.productionTimeSeconds % 60}s
                  {console.log('Rendering time:', activeProductGroup.productionTimeSeconds, 'for line', activeProductGroup.lineId) && null}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-indigo-700">No Product Group selected for this line</p>
              <button
                onClick={() => {
                  setSelectedProductGroup(null);
                  fetchAvailableGroups(); // Refresh product groups when opening selector
                  setShowGroupSelector(true);
                }}
                className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors"
                disabled={isLoading || !currentShift || !selectedLine}
              >
                {!currentShift ? 'No Active Shift' : !selectedLine ? 'Select Line First' : 'Select Product Group'}
              </button>
            </div>
          )}
        </div>
        
        {/* Mode Selection */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Production Mode</h3>
          {selectedLine && modes.map((mode) => {
            const currentLineMode = lineModes[selectedLine] || 'stop';
            const isSelected = currentLineMode === mode.id;
            console.log(`Rendering button for mode ${mode.id}, currentLineMode: ${currentLineMode}, isSelected: ${isSelected}`);
            return (
              <button
                key={mode.id}
                onClick={() => handleModeChange(mode.id)}
                disabled={isLoading}
                className={`
                  w-full text-left p-4 rounded-lg border-2 transition-all duration-200 min-h-[80px]
                  ${isSelected 
                    ? `${mode.selectedColor} text-white border-transparent shadow-lg transform scale-105` 
                    : `bg-white ${mode.borderColor} ${mode.hoverColor} hover:text-white hover:shadow-md`
                  }
                  ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className={`font-semibold text-base mb-1 ${isSelected ? '' : mode.textColor}`}>
                      {mode.name}
                      {mode.id === 'production' && isSelected && (
                        <span className="ml-2 inline-flex items-center">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                          </span>
                          <span className="ml-1 text-xs font-normal">Tracking Time</span>
                        </span>
                      )}
                      {mode.id === 'production-standby' && isSelected && (
                        <span className="ml-2 inline-flex items-center">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-pulse absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                          </span>
                          <span className="ml-1 text-xs font-normal">Standby</span>
                        </span>
                      )}
                    </h3>
                    <p className={`text-xs ${isSelected ? 'text-white opacity-90' : 'text-gray-600'}`}>
                      {mode.description}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="ml-2">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600">
          {selectedLine ? (
            <>
              <p className="font-semibold mb-1">
                {availableLines.find(l => l.id === selectedLine)?.lineName} Mode:
              </p>
              <p className={`font-bold text-base ${modes.find(m => m.id === (lineModes[selectedLine] || 'stop'))?.textColor}`}>
                {modes.find(m => m.id === (lineModes[selectedLine] || 'stop'))?.name || 'Unknown'}
              </p>
            </>
          ) : (
            <p className="text-gray-500">No line selected</p>
          )}
        </div>
      </div>
    </div>
    
    {/* Product Group Selector Modal */}
    {showGroupSelector && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
          <div className="flex items-center justify-between p-6">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Select Product Group</h2>
              {selectedLine && (
                <p className="text-sm text-gray-600 mt-1">
                  For Line: {availableLines.find(l => l.id === selectedLine)?.lineName}
                </p>
              )}
            </div>
            <button
              onClick={() => {
                setShowGroupSelector(false);
                setSelectedProductGroup(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          {/* Product Group List */}
          <div className="p-6 pt-0">
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableGroups.length > 0 ? (
                availableGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => setSelectedProductGroup(group.id)}
                    disabled={isLoading}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedProductGroup === group.id
                        ? 'bg-indigo-50 border-indigo-400 shadow-sm'
                        : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">{group.groupName}</p>
                        <p className="text-sm text-gray-600">{group.groupId}</p>
                        {group.infeedDisplayName && (
                          <p className="text-xs text-gray-500 mt-1">Infeed: {group.infeedDisplayName}</p>
                        )}
                      </div>
                      {selectedProductGroup === group.id && (
                        <span className="text-indigo-600">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No Product Groups available</p>
                  <p className="text-xs text-gray-400 mt-1">Please create Product Groups in Settings</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Footer Actions */}
          <div className="px-6 pb-6 flex justify-end">
            {/* Unassign button hidden for now - needs session management work
            <button
              onClick={() => {
                handleUnassignProductGroup();
              }}
              disabled={!activeProductGroup || isLoading}
              className={`px-4 py-2 font-medium rounded-md transition-colors ${
                activeProductGroup && !isLoading
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Unassign Current
            </button>
            */}
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowGroupSelector(false);
                  setSelectedProductGroup(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedProductGroup && selectedLine) {
                    handleProductGroupSwitch(selectedProductGroup);
                  }
                }}
                disabled={!selectedProductGroup || !selectedLine || isLoading}
                className={`px-4 py-2 font-medium rounded-md transition-colors ${
                  selectedProductGroup && selectedLine && !isLoading
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isLoading ? 'Assigning...' : 'Assign to Line'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* Line Selector Modal */}
    {showLineSelector && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="flex items-center justify-between p-6">
            <h2 className="text-lg font-bold text-gray-800">Select Production Line</h2>
            <button
              onClick={() => setShowLineSelector(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          {/* Line List */}
          <div className="px-6 pb-6">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {availableLines.map((line) => (
                <button
                  key={line.id}
                  onClick={() => {
                    console.log('Line selector clicked:', line.lineName, line.id);
                    console.log('Previous selected line:', selectedLine);
                    setSelectedLine(line.id);
                    setSelectedLineId(line.id); // Update context for Dashboard
                    setShowLineSelector(false);
                    console.log('New selected line set to:', line.id);
                    // Each line maintains its own product group
                    // The useEffect will handle updating the active product group
                  }}
                  className={`
                    w-full p-4 rounded-lg border-2 transition-all text-left
                    ${selectedLine === line.id 
                      ? 'bg-blue-50 border-blue-500 shadow-sm' 
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800">{line.lineName}</div>
                      {line.lineNumber && (
                        <div className="text-sm text-gray-600">Line #{line.lineNumber}</div>
                      )}
                    </div>
                    {selectedLine === line.id && (
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProductionModePanel;